"use client";

import { useTranslations } from "use-intl";

import { Price } from "@/components/Price";
import { needsDisambiguation, getCurrencyDisplayName } from "@/lib/format-price";
import { cn } from "@/lib/utils";

interface ForSaleBadgeProps {
  className?: string;
  count?: number;
  minPrice?: string | number;
  maxPrice?: string | number;
  currency?: string;
}

function ForSalePrice({ currency, max, min }: { currency: string; max: number; min: number }) {
  if (min === max) return <Price amount={min} currency={currency} plain />;
  return (
    <>
      <Price amount={min} currency={currency} plain />
      {"\u2013"}
      <Price amount={max} currency={currency} plain />
    </>
  );
}

export function ForSaleBadge({
  className,
  count,
  minPrice,
  maxPrice,
  currency,
}: ForSaleBadgeProps) {
  const t = useTranslations("shared.marketplace");

  // Rich mode: count provided but is 0 -- render nothing
  if (count !== undefined && count === 0) {
    return null;
  }

  // Simple mode: no count/price props at all -- backward compatible "For Sale" text
  if (count === undefined) {
    return (
      <span
        className={cn(
          "px-2 py-0.5 bg-green-600/20 text-green-600 text-xs font-bold uppercase tracking-widest rounded",
          className,
        )}
      >
        {t("forSale")}
      </span>
    );
  }

  const priceCurrency = minPrice !== undefined && maxPrice !== undefined ? currency : undefined;
  const label = priceCurrency
    ? t.rich("forSaleWithPrice", {
        count,
        price: () => (
          <ForSalePrice currency={priceCurrency} min={Number(minPrice)} max={Number(maxPrice)} />
        ),
      })
    : t("forSaleCount", { count });

  return (
    <span
      className={cn(
        "px-2 py-0.5 bg-green-600/20 text-green-600 text-xs font-bold rounded",
        className,
      )}
    >
      {label}
      {priceCurrency && needsDisambiguation(priceCurrency) && (
        <span className="text-muted-foreground font-normal normal-case ml-1">
          ({getCurrencyDisplayName(priceCurrency)})
        </span>
      )}
    </span>
  );
}
